import {
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, ExternalLink, ShieldCheck } from "lucide-react";

import {
  COOKIE_CONSENT_STORAGE_KEY,
  LEGACY_COOKIE_CONSENT_STORAGE_KEY,
  buildStoredCookieConsent,
  defaultCookieConsentPreferences,
  migrateLegacyCookieConsent,
  parseStoredCookieConsent,
  type CookieConsentPreferences,
  type StoredCookieConsent,
} from "@/lib/cookie-consent";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  CookieConsentContext,
  type CookieConsentContextType,
} from "@/lib/cookie-consent-context";

const defaultPreferences = defaultCookieConsentPreferences();

const CONSENT_BANNER_DELAY_MS = 700;

const loadStoredConsent = (): StoredCookieConsent | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const storedConsent = parseStoredCookieConsent(
    window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY),
  );

  if (storedConsent) {
    return storedConsent;
  }

  const legacyConsent = migrateLegacyCookieConsent(
    window.localStorage.getItem(LEGACY_COOKIE_CONSENT_STORAGE_KEY),
  );

  if (legacyConsent) {
    window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(legacyConsent));
    window.localStorage.removeItem(LEGACY_COOKIE_CONSENT_STORAGE_KEY);
  }

  return legacyConsent;
};

const persistConsent = (consent: StoredCookieConsent) => {
  window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(consent));
  window.localStorage.removeItem(LEGACY_COOKIE_CONSENT_STORAGE_KEY);
};

const consentCardClassName = "rounded-sm border border-border bg-card/80 p-4 backdrop-blur-sm";

export const CookieConsentProvider = ({ children }: { children: ReactNode }) => {
  const [consent, setConsent] = useState<StoredCookieConsent | null>(() => loadStoredConsent());
  const [bannerVisible, setBannerVisible] = useState(false);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const [draftPreferences, setDraftPreferences] =
    useState<CookieConsentPreferences>(defaultPreferences);

  const hasAnswered = consent !== null;
  const preferences = consent?.preferences ?? defaultPreferences;

  useEffect(() => {
    setDraftPreferences(preferences);
  }, [preferences]);

  useEffect(() => {
    if (hasAnswered) {
      setBannerVisible(false);
      return;
    }

    const timer = window.setTimeout(() => setBannerVisible(true), CONSENT_BANNER_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [hasAnswered]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== COOKIE_CONSENT_STORAGE_KEY) {
        return;
      }

      setConsent(parseStoredCookieConsent(event.newValue));
    };

    window.addEventListener("storage", handleStorage);

    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const commitConsent = (nextConsent: StoredCookieConsent) => {
    persistConsent(nextConsent);
    setConsent(nextConsent);
    setBannerVisible(false);
    setIsPreferencesOpen(false);
  };

  const acceptAll = () => {
    commitConsent(buildStoredCookieConsent({ externalMedia: true }));
  };

  const rejectOptional = () => {
    commitConsent(buildStoredCookieConsent({ externalMedia: false }));
  };

  const savePreferences = (
    nextPreferences: Partial<Omit<CookieConsentPreferences, "necessary">>,
  ) => {
    commitConsent(
      buildStoredCookieConsent({
        externalMedia: nextPreferences.externalMedia ?? draftPreferences.externalMedia,
      }),
    );
  };

  const openPreferences = () => {
    setDraftPreferences(preferences);
    setIsPreferencesOpen(true);
  };

  const closePreferences = () => {
    setDraftPreferences(preferences);
    setIsPreferencesOpen(false);
  };

  const value: CookieConsentContextType = {
    consent,
    hasAnswered,
    preferences,
    isCategoryEnabled: (category) => preferences[category],
    acceptAll,
    rejectOptional,
    savePreferences,
    openPreferences,
    closePreferences,
    isPreferencesOpen,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}

      <AnimatePresence>
        {bannerVisible && (
          <motion.aside
            aria-label="Cookie-Einstellungen"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed inset-x-0 bottom-0 z-[90] p-4"
          >
            <div className="container mx-auto max-w-5xl">
              <div className="rounded-sm border border-border bg-background/95 p-5 shadow-2xl backdrop-blur-xl">
                <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
                  <div className="flex gap-4 lg:flex-1">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/10">
                      <Cookie className="h-6 w-6 text-primary" />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-primary">
                        <ShieldCheck className="h-4 w-4" />
                        <span className="font-display text-sm uppercase tracking-[0.22em]">
                          Datenschutz-Einstellungen
                        </span>
                      </div>
                      <h3 className="font-display text-2xl font-bold uppercase tracking-wide text-foreground">
                        Externe Inhalte bleiben aus, bis Sie zustimmen
                      </h3>
                      <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
                        Wir nutzen nur technisch notwendige Speicherungen, um Ihre Entscheidung zu
                        merken. Die Google-Maps-Karte wird erst nach Ihrer ausdrücklichen
                        Einwilligung geladen. Analytics- oder Marketing-Cookies sind derzeit nicht
                        eingebunden.
                      </p>
                      <p className="text-xs leading-relaxed text-muted-foreground">
                        Details finden Sie in unserer{" "}
                        <Link to="/datenschutz" className="text-primary hover:underline">
                          Datenschutzerklärung
                        </Link>{" "}
                        und auf der{" "}
                        <Link to="/cookies" className="text-primary hover:underline">
                          Cookie-Seite
                        </Link>
                        .
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-3 lg:w-[420px]">
                    <button
                      type="button"
                      onClick={rejectOptional}
                      className="min-h-12 rounded-sm border border-border px-4 py-3 text-sm font-display uppercase tracking-[0.14em] text-foreground transition-colors hover:bg-secondary"
                    >
                      Alles ablehnen
                    </button>
                    <button
                      type="button"
                      onClick={openPreferences}
                      className="min-h-12 rounded-sm border border-border px-4 py-3 text-sm font-display uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                    >
                      Einstellungen
                    </button>
                    <button
                      type="button"
                      onClick={acceptAll}
                      className="min-h-12 rounded-sm border border-primary/50 bg-primary/10 px-4 py-3 text-sm font-display uppercase tracking-[0.14em] text-primary transition-colors hover:bg-primary/20"
                    >
                      Alles akzeptieren
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      <Dialog open={isPreferencesOpen} onOpenChange={(open) => (open ? openPreferences() : closePreferences())}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-background sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase tracking-wide text-foreground">
              Cookie- und Datenschutz-Einstellungen
            </DialogTitle>
            <DialogDescription className="leading-relaxed">
              Sie können optionale Dienste jederzeit aktivieren oder wieder deaktivieren. Notwendige
              Speicherungen bleiben aktiv, damit Ihre Auswahl respektiert wird.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className={consentCardClassName}>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="font-display text-lg font-bold uppercase tracking-wide text-foreground">
                    Notwendig
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    Speichert nur Ihre Datenschutzeinstellung lokal im Browser, damit das Banner
                    nicht bei jedem Aufruf erneut erscheint.
                  </p>
                </div>
                <span className="inline-flex w-fit items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-display uppercase tracking-[0.16em] text-primary">
                  Immer aktiv
                </span>
              </div>
            </div>

            <div className={consentCardClassName}>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="font-display text-lg font-bold uppercase tracking-wide text-foreground">
                      Externe Medien
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      Lädt eingebettete Inhalte von Drittanbietern. Aktuell betrifft das nur die
                      Google-Maps-Karte für den Standort in Nalbach.
                    </p>
                    <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                      <ExternalLink className="h-3.5 w-3.5" />
                      <span>Bei Aktivierung kann Google insbesondere Ihre IP-Adresse verarbeiten.</span>
                    </div>
                  </div>
                  <label className="inline-flex cursor-pointer items-center gap-3 self-start">
                    <span className="text-sm text-muted-foreground">Deaktiviert</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={draftPreferences.externalMedia}
                      onClick={() =>
                        setDraftPreferences((currentDraft) => ({
                          ...currentDraft,
                          externalMedia: !currentDraft.externalMedia,
                        }))
                      }
                      className={`relative inline-flex h-7 w-12 items-center rounded-full border transition-colors ${
                        draftPreferences.externalMedia
                          ? "border-primary bg-primary/20"
                          : "border-border bg-secondary"
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-foreground transition-transform ${
                          draftPreferences.externalMedia ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                      <span className="sr-only">Externe Medien aktivieren</span>
                    </button>
                    <span className="text-sm text-foreground">Aktiviert</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="rounded-sm border border-dashed border-border px-4 py-3 text-xs leading-relaxed text-muted-foreground">
              Ihre Auswahl können Sie jederzeit über den Footer-Link{" "}
              <Link to="/cookies" className="text-primary hover:underline">
                Cookies
              </Link>{" "}
              oder innerhalb der{" "}
              <Link to="/datenschutz" className="text-primary hover:underline">
                Datenschutzerklärung
              </Link>{" "}
              ändern.
            </div>
          </div>

          <DialogFooter className="gap-3 sm:justify-between sm:space-x-0">
            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={rejectOptional}
                className="rounded-sm border border-border px-4 py-3 text-sm font-display uppercase tracking-[0.14em] text-foreground transition-colors hover:bg-secondary"
              >
                Alles ablehnen
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="rounded-sm border border-primary/50 bg-primary/10 px-4 py-3 text-sm font-display uppercase tracking-[0.14em] text-primary transition-colors hover:bg-primary/20"
              >
                Alles akzeptieren
              </button>
            </div>

            <button
              type="button"
              onClick={() => savePreferences({ externalMedia: draftPreferences.externalMedia })}
              className="rounded-sm border border-primary bg-primary px-4 py-3 text-sm font-display uppercase tracking-[0.14em] text-primary-foreground transition-colors hover:bg-primary/85"
            >
              Auswahl speichern
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CookieConsentContext.Provider>
  );
};
